import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  fetchBlogs, 
  selectBlogs, 
  selectBlogLoading, 
  selectBlogError, 
  selectBlogPagination, 
  setFilters 
} from '../redux/slices/blogSlice';
import { 
  FiSearch, FiFilter, FiGrid, FiList, FiClock, FiHeart, 
  FiChevronLeft, FiChevronRight, FiSave, FiTrash2, FiX, FiBookmark,
  FiArrowRight
} from 'react-icons/fi';

const Blog = () => {
  const dispatch = useDispatch();
  const topRef = useRef(null);

  // -- State --
  const [currentFilters, setCurrentFilters] = useState({
    category: '',
    search: '',
    sortBy: 'date',
    sortOrder: 'desc',
    page: 1
  });
  
  const [viewMode, setViewMode] = useState('grid');
  const [savedSearches, setSavedSearches] = useState([]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);

  // -- Redux Selectors --
  const blogs = useSelector(selectBlogs) || [];
  const loading = useSelector(selectBlogLoading);
  const error = useSelector(selectBlogError);
  const pagination = useSelector(selectBlogPagination);

  const safeBlogs = Array.isArray(blogs) ? blogs : [];

  // -- Effects --
  useEffect(() => {
    dispatch(fetchBlogs(currentFilters));
  }, [dispatch, currentFilters]);

  useEffect(() => {
    const saved = localStorage.getItem('savedBlogSearches');
    if (saved) {
      setSavedSearches(JSON.parse(saved));
    }
  }, []);

  // -- Handlers --
  const handleFilterChange = (newFilters) => {
    const updatedFilters = { ...currentFilters, ...newFilters, page: 1 };
    setCurrentFilters(updatedFilters);
    dispatch(setFilters(updatedFilters));
  };

  const handlePageChange = (newPage) => {
    const updatedFilters = { ...currentFilters, page: newPage };
    setCurrentFilters(updatedFilters);
    dispatch(fetchBlogs(updatedFilters));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const searchValue = e.target.search.value;
    handleFilterChange({ search: searchValue });
    if (searchValue) {
      toast.success(`Results for: "${searchValue}"`);
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      category: '',
      search: '',
      sortBy: 'date',
      sortOrder: 'desc',
      page: 1
    };
    setCurrentFilters(clearedFilters);
    dispatch(setFilters(clearedFilters));
    toast.success('Filters reset');
  };

  const saveCurrentSearch = () => {
    if (!currentFilters.search && !currentFilters.category) {
      toast.error('Apply a filter or search first');
      return;
    }

    const searchName = prompt('Name this search configuration:');
    if (searchName && searchName.trim()) {
      const newSearch = {
        id: Date.now(),
        name: searchName.trim(),
        filters: { ...currentFilters },
        createdAt: new Date().toISOString()
      };
      
      const updatedSaved = [newSearch, ...savedSearches].slice(0, 10); 
      setSavedSearches(updatedSaved);
      localStorage.setItem('savedBlogSearches', JSON.stringify(updatedSaved));
      toast.success('Search configuration saved');
    }
  };

  const loadSavedSearch = (savedSearch) => {
    setCurrentFilters(savedSearch.filters);
    dispatch(setFilters(savedSearch.filters));
    setShowSavedSearches(false);
    toast.success(`Applied: ${savedSearch.name}`);
  };

  const deleteSavedSearch = (e, searchId) => {
    e.stopPropagation();
    const updatedSaved = savedSearches.filter(search => search.id !== searchId);
    setSavedSearches(updatedSaved);
    localStorage.setItem('savedBlogSearches', JSON.stringify(updatedSaved));
    toast.success('Saved search removed');
  };

  // -- Helpers --
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getReadingTime = (content) => {
    if (!content) return 1;
    const words = content.split(' ').length;
    return Math.ceil(words / 200);
  };

  const categories = [
    { value: '', label: 'All Topics' },
    { value: 'market-trends', label: 'Market Trends' },
    { value: 'buying-guide', label: 'Buying Guide' },
    { value: 'selling-tips', label: 'Selling Tips' },
    { value: 'investment', label: 'Investment' },
    { value: 'financing', label: 'Financing' },
    { value: 'lifestyle', label: 'Lifestyle' },
  ];

  // -- Sub-Components --
  const BlogCard = ({ blog, index }) => {
    const isList = viewMode === 'list';
    
    return (
      <motion.article
        layout
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95 }}
        whileHover={{ y: -5 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        className={`group relative bg-white rounded-2xl overflow-hidden border border-slate-100/60 shadow-lg hover:shadow-2xl hover:shadow-indigo-500/10 transition-all duration-300 ${
          isList ? 'flex flex-col sm:flex-row gap-6 p-5 sm:items-center' : 'flex flex-col h-full'
        }`}
      >
        {/* Image Container */}
        <Link 
          to={`/blog/${blog.slug}`}
          className={`relative overflow-hidden block group-hover:brightness-105 transition-all ${
            isList ? 'w-full sm:w-56 md:w-72 h-56 sm:h-44 rounded-xl shrink-0' : 'w-full aspect-[4/3]'
          }`}
        >
          <div className="absolute inset-0 bg-slate-200 animate-pulse" />
          <motion.img
            src={blog.featuredImage || blog.image || '/api/placeholder/600/400'}
            alt={blog.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            onError={(e) => { e.target.src = 'https://via.placeholder.com/600x400?text=Real+Estate'; }}
          />
          {/* Badge */}
          <div className="absolute top-4 left-4 z-10">
             <span className="px-3 py-1.5 rounded-lg bg-white/95 backdrop-blur-md text-xs font-bold text-indigo-600 shadow-lg tracking-wide">
                {categories.find(c => c.value === blog.category)?.label || blog.category || 'Article'}
             </span>
          </div>
        </Link>

        {/* Content Container */}
        <div className={`flex flex-col flex-grow ${isList ? 'py-1' : 'p-6'}`}>
          <div className="flex items-center gap-3 text-xs font-medium text-slate-500 mb-4">
            <span className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-md">
              <FiClock className="w-3.5 h-3.5 text-indigo-500" />
              {formatDate(blog.publishedAt || blog.createdAt)}
            </span>
            <span className="w-1 h-1 rounded-full bg-slate-300" />
            <span>{getReadingTime(blog.content)} min read</span>
          </div>

          <h2 className={`font-bold text-slate-900 leading-tight mb-3 group-hover:text-indigo-600 transition-colors ${
            isList ? 'text-2xl' : 'text-xl line-clamp-2'
          }`}>
            <Link to={`/blog/${blog.slug}`}>
              {blog.title}
            </Link>
          </h2>

          <p className={`text-slate-600 text-sm leading-relaxed mb-6 ${
            isList ? 'line-clamp-2 md:line-clamp-3' : 'line-clamp-3 flex-grow'
          }`}>
            {blog.excerpt || (blog.content?.substring(0, 150) + '...')}
          </p>

          {/* Footer */}
          <div className="pt-4 border-t border-slate-100 flex items-center justify-between mt-auto">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold ring-2 ring-white shadow-md">
                {blog.Author?.firstName?.charAt(0) || 'A'}
              </div>
              <span className="text-xs font-semibold text-slate-700">
                {blog.Author?.firstName} {blog.Author?.lastName}
              </span>
            </div>
            
            <Link 
              to={`/blog/${blog.slug}`}
              className="flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-700 transition-colors"
            >
              Read More <FiArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </motion.article>
    );
  };

  return (
    // Main Wrapper: pt-32 ensures content starts below fixed header
    <div ref={topRef} className="min-h-screen bg-slate-50 pt-32 pb-20 relative overflow-hidden">
      
      {/* Background Ambience */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-200/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 mix-blend-multiply" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-200/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 mix-blend-multiply" />
      </div>

      {/* --- Fixed Header Section --- */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200 shadow-sm transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            
            {/* Title & Sub */}
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-lg">I</span>
                Insights<span className="text-indigo-600">.</span>
              </h1>
            </div>

            {/* Actions Toolbar */}
            <div className="flex items-center gap-3 self-end md:self-auto">
              
              {/* Saved Searches */}
              <div className="relative">
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowSavedSearches(!showSavedSearches)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    showSavedSearches || savedSearches.length > 0 
                    ? 'bg-indigo-50 border-indigo-200 text-indigo-700' 
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <FiBookmark className="w-4 h-4" />
                  <span className="hidden sm:inline">Saved</span>
                  {savedSearches.length > 0 && (
                    <span className="w-5 h-5 bg-indigo-600 text-white text-[10px] font-bold flex items-center justify-center rounded-full ml-1">
                      {savedSearches.length}
                    </span>
                  )}
                </motion.button>

                {/* Dropdown */}
                <AnimatePresence>
                  {showSavedSearches && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 p-2 z-50 origin-top-right ring-1 ring-black/5"
                    >
                      <div className="px-4 py-3 border-b border-slate-50 flex justify-between items-center">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Your Filters</span>
                        <button onClick={() => setShowSavedSearches(false)} className="text-slate-400 hover:text-slate-600"><FiX /></button>
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1 custom-scrollbar">
                        {savedSearches.length === 0 ? (
                          <div className="p-6 text-center text-sm text-slate-400">
                            No saved searches yet. <br/> Apply filters and click save.
                          </div>
                        ) : (
                          savedSearches.map((search) => (
                            <div key={search.id} onClick={() => loadSavedSearch(search)} className="flex items-center justify-between p-3 hover:bg-indigo-50 rounded-xl cursor-pointer group transition-colors mx-1 my-1">
                              <div>
                                <div className="font-bold text-slate-700 text-sm">{search.name}</div>
                                <div className="text-[10px] text-slate-400 mt-0.5">{new Date(search.createdAt).toLocaleDateString()}</div>
                              </div>
                              <button 
                                onClick={(e) => deleteSavedSearch(e, search.id)}
                                className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* View Toggle */}
              <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <FiGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white shadow text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  <FiList className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* --- Floating Filter Bar --- */}
      <div className="fixed top-[88px] left-0 right-0 z-40 px-4 sm:px-6 pointer-events-none">
        <div className="max-w-7xl mx-auto pointer-events-auto">
          <motion.div 
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="bg-white/80 backdrop-blur-md border border-slate-200/60 shadow-xl shadow-slate-200/50 rounded-2xl p-4 transform translate-y-2"
          >
            <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
              
              {/* Search Input */}
              <div className="flex-grow relative group">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors w-5 h-5" />
                <input
                  type="text"
                  name="search"
                  defaultValue={currentFilters.search}
                  placeholder="Find articles, guides, insights..."
                  className="w-full bg-slate-50/50 hover:bg-slate-50 focus:bg-white border border-slate-200 focus:border-indigo-500 rounded-xl pl-12 pr-4 py-3 text-slate-700 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium text-sm"
                />
              </div>

              {/* Filters Group */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 lg:w-auto">
                {/* Category */}
                <div className="relative col-span-2 md:col-span-1">
                  <select
                    value={currentFilters.category}
                    onChange={(e) => handleFilterChange({ category: e.target.value })}
                    className="w-full appearance-none bg-slate-50/50 hover:bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-700 text-sm font-medium outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all cursor-pointer"
                  >
                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                  <FiFilter className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none w-4 h-4" />
                </div>

                {/* Sort By */}
                <div className="relative col-span-2 md:col-span-1">
                  <select
                    value={`${currentFilters.sortBy}-${currentFilters.sortOrder}`}
                    onChange={(e) => {
                      const [sortBy, sortOrder] = e.target.value.split('-');
                      handleFilterChange({ sortBy, sortOrder });
                    }}
                    className="w-full appearance-none bg-slate-50/50 hover:bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-700 text-sm font-medium outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all cursor-pointer"
                  >
                    <option value="date-desc">Newest First</option>
                    <option value="date-asc">Oldest First</option>
                    <option value="views-desc">Most Popular</option>
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                  </div>
                </div>

                {/* Actions */}
                <div className="col-span-2 flex gap-2">
                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-6 rounded-xl shadow-lg shadow-indigo-500/20 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    Search
                  </motion.button>
                  
                  {(currentFilters.search || currentFilters.category) && (
                    <>
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={saveCurrentSearch}
                        className="bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200 p-3 rounded-xl transition-colors"
                        title="Save Search"
                      >
                        <FiSave className="w-5 h-5" />
                      </motion.button>
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        type="button"
                        onClick={clearFilters}
                        className="bg-slate-100 text-slate-500 hover:bg-slate-200 border border-slate-200 p-3 rounded-xl transition-colors"
                        title="Clear Filters"
                      >
                        <FiX className="w-5 h-5" />
                      </motion.button>
                    </>
                  )}
                </div>
              </div>
            </form>
          </motion.div>
        </div>
      </div>

      {/* --- Main Content --- */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10 mt-12">
        
        {/* Error State */}
        {error && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-red-50 border border-red-100 p-8 text-center mb-12 shadow-sm">
            <h3 className="text-red-800 font-bold text-lg mb-2">Unable to load articles</h3>
            <p className="text-red-600 mb-6 text-sm">{error}</p>
            <button 
              onClick={() => dispatch(fetchBlogs(currentFilters))}
              className="px-6 py-2.5 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 font-medium transition-colors text-sm shadow-sm"
            >
              Try Again
            </button>
          </motion.div>
        )}

        {/* Loading State */}
        {loading && safeBlogs.length === 0 ? (
          <div className={`grid gap-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'grid-cols-1'}`}>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <div key={n} className={`bg-white rounded-2xl p-4 border border-slate-100 shadow-sm ${viewMode === 'list' ? 'flex gap-6 h-48' : 'h-[420px]'}`}>
                <div className={`bg-slate-100 rounded-xl animate-pulse ${viewMode === 'list' ? 'w-64 h-full' : 'w-full h-48 mb-4'}`} />
                <div className="flex-1 space-y-3 py-2">
                  <div className="flex gap-2">
                     <div className="h-4 bg-slate-100 rounded w-20 animate-pulse" />
                     <div className="h-4 bg-slate-100 rounded w-12 animate-pulse" />
                  </div>
                  <div className="h-6 bg-slate-100 rounded w-3/4 animate-pulse mt-4" />
                  <div className="h-6 bg-slate-100 rounded w-1/2 animate-pulse" />
                  <div className="h-20 bg-slate-100 rounded w-full animate-pulse mt-2" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Empty State */}
            {safeBlogs.length === 0 && !loading && (
              <div className="text-center py-24 bg-white/60 backdrop-blur-sm rounded-3xl border border-dashed border-slate-300">
                <div className="w-24 h-24 bg-gradient-to-tr from-indigo-50 to-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <FiSearch className="w-10 h-10 text-indigo-300" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">No articles found</h3>
                <p className="text-slate-500 mb-8 max-w-md mx-auto text-sm">
                  We couldn't find any articles matching your current filters. 
                  <br />Try adjusting your search or categories.
                </p>
                <button 
                  onClick={clearFilters}
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200 text-sm"
                >
                  Clear all filters
                </button>
              </div>
            )}

            {/* Results Grid */}
            <AnimatePresence mode="wait">
              <motion.div
                key={viewMode}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className={`grid gap-8 ${
                  viewMode === 'grid' 
                    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                    : 'grid-cols-1 max-w-4xl mx-auto'
                }`}
              >
                {safeBlogs.map((blog, index) => (
                  <BlogCard key={blog.id} blog={blog} index={index} />
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-20 pt-8 border-t border-slate-200/60 flex flex-col sm:flex-row items-center justify-between gap-4">
                <span className="text-sm text-slate-500 font-medium bg-white px-4 py-2 rounded-lg shadow-sm border border-slate-100">
                  Page <span className="text-indigo-600 font-bold">{currentFilters.page}</span> of {pagination.totalPages}
                </span>
                
                <div className="flex items-center gap-2">
                  <button
                    disabled={currentFilters.page <= 1}
                    onClick={() => handlePageChange(currentFilters.page - 1)}
                    className="p-3 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-indigo-600 hover:border-indigo-100 hover:shadow-md disabled:opacity-50 disabled:hover:shadow-none transition-all"
                  >
                    <FiChevronLeft />
                  </button>

                  <div className="flex gap-1 bg-white p-1 rounded-xl border border-slate-100 shadow-sm">
                    {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                      let p = i + 1;
                      if (pagination.totalPages > 5 && currentFilters.page > 3) {
                        p = currentFilters.page - 2 + i;
                      }
                      if (p > pagination.totalPages) return null;

                      return (
                        <button
                          key={p}
                          onClick={() => handlePageChange(p)}
                          className={`w-10 h-10 rounded-lg font-bold text-sm transition-all ${
                            currentFilters.page === p
                              ? 'bg-indigo-600 text-white shadow-md'
                              : 'text-slate-500 hover:bg-slate-50'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    disabled={currentFilters.page >= pagination.totalPages}
                    onClick={() => handlePageChange(currentFilters.page + 1)}
                    className="p-3 rounded-xl border border-slate-200 bg-white text-slate-600 hover:text-indigo-600 hover:border-indigo-100 hover:shadow-md disabled:opacity-50 disabled:hover:shadow-none transition-all"
                  >
                    <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Blog;